import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Checkbox } from 'components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { 
  ArrowLeft, Shield, Lock, Eye, FileText, AlertTriangle, 
  CheckCircle, Users, Phone, Laptop, Trash2, MessageSquare,
  Download, Printer, BookOpen, Scale, Heart
} from 'lucide-react';

const HIPAA_CHECKLIST = [
  { id: 1, text: "I understand what PHI (Protected Health Information) is", category: "basics" },
  { id: 2, text: "I only access PHI when necessary for care duties", category: "access" },
  { id: 3, text: "I never share PHI with unauthorized persons", category: "sharing" },
  { id: 4, text: "I use secure methods to communicate health information", category: "communication" },
  { id: 5, text: "I properly dispose of documents containing PHI", category: "disposal" },
  { id: 6, text: "I keep my login credentials secure and private", category: "security" },
  { id: 7, text: "I lock my device when stepping away", category: "security" },
  { id: 8, text: "I report any suspected breaches immediately", category: "reporting" },
  { id: 9, text: "I have completed HIPAA training", category: "training" },
  { id: 10, text: "I understand the penalties for HIPAA violations", category: "penalties" }
];

export default function HIPAAGuide() {
  const navigate = useNavigate();
  // Load checked items from localStorage on initial render
  const [checkedItems, setCheckedItems] = useState(() => {
    try {
      const saved = localStorage.getItem('hipaa_checklist_progress');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save to localStorage whenever checkedItems changes
  useEffect(() => {
    try {
      localStorage.setItem('hipaa_checklist_progress', JSON.stringify(checkedItems));
    } catch (e) {
      console.warn('Could not save checklist progress:', e);
    }
  }, [checkedItems]);

  const completionPercent = Math.round((checkedItems.length / HIPAA_CHECKLIST.length) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <button onClick={() => navigate('/resources')} className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Resource Center
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-teal-500/20 flex items-center justify-center">
              <Shield className="w-8 h-8 text-teal-400" />
            </div>
            <div>
              <Badge className="bg-teal-500/20 text-teal-300 mb-2">Legal & Compliance</Badge>
              <h1 className="text-2xl sm:text-3xl font-bold font-['Manrope']">HIPAA Compliance Guide</h1>
              <p className="text-slate-400 mt-1">Protecting Patient Privacy in Home Care</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Quick Overview */}
        <Card className="border-teal-200 bg-teal-50/50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">What is HIPAA?</h2>
                <p className="text-slate-600">
                  The <strong>Health Insurance Portability and Accountability Act (HIPAA)</strong> is a federal law 
                  that protects sensitive patient health information. As a caregiver, you handle Protected Health 
                  Information (PHI) daily, making HIPAA compliance essential to your role.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Principles */}
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Scale className="w-6 h-6 text-teal-500" />
            Key HIPAA Principles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-5">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-3">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Privacy Rule</h3>
                <p className="text-sm text-slate-600">
                  Protects all individually identifiable health information. Only access PHI when necessary for care.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mb-3">
                  <Lock className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Security Rule</h3>
                <p className="text-sm text-slate-600">
                  Requires safeguards to protect electronic PHI. Use secure passwords and encrypted devices.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Breach Notification</h3>
                <p className="text-sm text-slate-600">
                  Report any unauthorized disclosure immediately. Quick reporting minimizes harm and penalties.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* What is PHI */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-500" />
              What is Protected Health Information (PHI)?
            </CardTitle>
            <CardDescription>
              PHI includes any health information that can identify an individual
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-slate-900 mb-3">PHI Includes:</h4>
                <ul className="space-y-2">
                  {[
                    "Names and addresses",
                    "Birth dates and Social Security numbers",
                    "Medical records and diagnoses",
                    "Medication lists and prescriptions",
                    "Treatment plans and care notes",
                    "Insurance information",
                    "Photos and videos of the patient",
                    "Any health-related conversations"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Never Share PHI With:
                </h4>
                <ul className="space-y-2">
                  {[
                    "Friends or family members (unless authorized)",
                    "Other caregivers not involved in care",
                    "Social media or public forums",
                    "Employers (without consent)",
                    "Anyone not directly involved in the patient's care"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-red-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Guidelines Accordion */}
        <Card>
          <CardHeader>
            <CardTitle>Caregiver HIPAA Guidelines</CardTitle>
            <CardDescription>Click each section to expand detailed guidance</CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="communication">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-purple-500" />
                    <span>Secure Communication</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 space-y-3 pt-2">
                  <p><strong>Do:</strong></p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Use ElderLink's secure messaging for health discussions</li>
                    <li>Verify you're speaking to authorized individuals before sharing info</li>
                    <li>Use encrypted email when sending health documents</li>
                    <li>Make calls in private areas where you won't be overheard</li>
                  </ul>
                  <p className="mt-3"><strong>Don't:</strong></p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Send PHI via regular text messages or unencrypted email</li>
                    <li>Discuss patient details in public places</li>
                    <li>Leave voicemails with detailed health information</li>
                    <li>Use social media to discuss patients (even without names)</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="devices">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Laptop className="w-5 h-5 text-blue-500" />
                    <span>Device Security</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 space-y-3 pt-2">
                  <p><strong>Best Practices:</strong></p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Use strong, unique passwords (at least 12 characters)</li>
                    <li>Enable two-factor authentication when available</li>
                    <li>Lock your device when stepping away (even briefly)</li>
                    <li>Keep your operating system and apps updated</li>
                    <li>Use device encryption (usually enabled by default)</li>
                    <li>Never save passwords in browsers on shared devices</li>
                    <li>Log out of ElderLink when done with your session</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="disposal">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Trash2 className="w-5 h-5 text-red-500" />
                    <span>Proper Disposal of PHI</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 space-y-3 pt-2">
                  <p><strong>Paper Documents:</strong></p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Shred all documents containing PHI before disposal</li>
                    <li>Never throw PHI documents in regular trash</li>
                    <li>Use cross-cut shredders when possible</li>
                  </ul>
                  <p className="mt-3"><strong>Electronic Data:</strong></p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Delete files securely (empty trash after deletion)</li>
                    <li>Clear browser history and cache after accessing PHI</li>
                    <li>Wipe devices before disposal or transfer</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="breaches">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    <span>Reporting Breaches</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 space-y-3 pt-2">
                  <p><strong>What constitutes a breach:</strong></p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Lost or stolen devices containing PHI</li>
                    <li>Accidentally sending PHI to wrong recipient</li>
                    <li>Unauthorized access to patient records</li>
                    <li>Discussing PHI with unauthorized persons</li>
                    <li>PHI documents found in unsecured locations</li>
                  </ul>
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="font-medium text-amber-800">If you suspect a breach:</p>
                    <ol className="list-decimal pl-6 mt-2 space-y-1 text-amber-700">
                      <li>Report immediately to your supervisor</li>
                      <li>Document what happened and when</li>
                      <li>Do not attempt to "fix" or hide the breach</li>
                      <li>Cooperate fully with any investigation</li>
                    </ol>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="penalties">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Scale className="w-5 h-5 text-slate-500" />
                    <span>Penalties for Violations</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 space-y-3 pt-2">
                  <p>HIPAA violations can result in serious consequences:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-medium text-slate-900">Civil Penalties</p>
                      <p className="text-sm mt-1">$100 - $50,000 per violation, up to $1.5M per year</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-medium text-slate-900">Criminal Penalties</p>
                      <p className="text-sm mt-1">Up to $250,000 fine and 10 years imprisonment</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm">
                    <strong>Note:</strong> Even unintentional violations can result in penalties. 
                    When in doubt, always err on the side of caution and ask your supervisor.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Compliance Checklist */}
        <Card id="checklist">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-500" />
                  HIPAA Compliance Checklist
                </CardTitle>
                <CardDescription>
                  Review and check off each item to ensure you're compliant. Your progress is saved automatically.
                </CardDescription>
              </div>
              {checkedItems.length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setCheckedItems([])}
                  className="text-slate-500 hover:text-slate-700"
                  data-testid="reset-checklist"
                >
                  Reset Progress
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Your Progress</span>
                <span className="text-sm font-medium text-teal-600">{completionPercent}% Complete</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-teal-500 transition-all duration-300"
                  style={{ width: `${completionPercent}%` }}
                />
              </div>
            </div>
            
            <div className="space-y-3">
              {HIPAA_CHECKLIST.map((item) => (
                <label 
                  key={item.id}
                  htmlFor={`checkbox-${item.id}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                    checkedItems.includes(item.id) 
                      ? 'bg-teal-50 border-teal-200' 
                      : 'bg-white border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`checklist-item-${item.id}`}
                >
                  <Checkbox 
                    id={`checkbox-${item.id}`}
                    checked={checkedItems.includes(item.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setCheckedItems(prev => [...prev, item.id]);
                      } else {
                        setCheckedItems(prev => prev.filter(i => i !== item.id));
                      }
                    }}
                  />
                  <span className={checkedItems.includes(item.id) ? 'text-teal-700' : 'text-slate-700'}>
                    {item.text}
                  </span>
                </label>
              ))}
            </div>
            
            {completionPercent === 100 && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="font-medium text-green-800">Excellent! You've completed the HIPAA checklist.</p>
                <p className="text-sm text-green-700 mt-1">Remember to review these guidelines regularly.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Reference Card */}
        <Card className="border-slate-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Remember</h3>
                  <p className="text-sm text-slate-600">
                    HIPAA compliance protects both your patients and you. When in doubt, ask!
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={() => window.print()}>
                <Printer className="w-4 h-4" />
                Print Guide
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
